if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.hideErrorMessage(); // Clear any existing error messages

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const extraItems = this.getExtraVariantItems(); // collect extra variants before main add
      console.log('Found extra items to add:', extraItems);

      // Chain promises to add main product first, then extra items
      this.addMainProduct()
        .then(response => {
          if (response.status) {
            // Show main product error in the buttons error area
            const buttonsError = this.querySelector('.product-form__buttons-error');
            if (buttonsError) {
              buttonsError.innerHTML = `<p>${response.description}</p>`;
              buttonsError.removeAttribute('hidden');
            }
            
            this.handleErrorMessage(response.description);
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return null;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return null;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return null;
          }

          // If we have extra items, add them
          if (extraItems.length > 0) {
            return this.addAdditionalVariants(extraItems)
              .then(result => {
                // If there were errors with bundle items, show message but don't break the flow
                if (result.unavailableItems && result.unavailableItems.length > 0) {
                  this.handleBundleItemsError(result.unavailableItems);
                } else {
                  this.hideErrorMessage(); // Clear any error messages if all items were added successfully
                }

                this.error = false;
                const quickAddModal = this.closest('quick-add-modal');
                // Use the response from result.response if available, otherwise use the original response
                const finalResponse = result.response || response;
                
                if (quickAddModal) {
                  this.cart.renderContents(finalResponse);
                  quickAddModal.hide(true);
                } else {
                  this.cart.renderContents(finalResponse);
                }
                return finalResponse;
              })
              .catch(error => {
                // Show any unexpected errors in the buttons error area
                const buttonsError = this.querySelector('.product-form__buttons-error');
                if (buttonsError) {
                  buttonsError.innerHTML = '<p>There was an error adding items to your cart. Not all products are added to cart.</p>';
                  buttonsError.removeAttribute('hidden');
                }
                console.error('Error adding bundle items:', error);
                return response; // Return the original response to continue the flow
              });
          } else {
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              this.cart.renderContents(response);
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            return response;
          }
        })
        .catch((e) => {
          // Display unexpected errors in the buttons error area
          const buttonsError = this.querySelector('.product-form__buttons-error');
          if (buttonsError) {
            buttonsError.innerHTML = '<p>There was an error adding items to your cart. Please try again.</p>';
            buttonsError.removeAttribute('hidden');
          }
          console.error('Error during cart update:', e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    // Show error message when bundle items are unavailable
    handleBundleItemsError(unavailableItems) {
      if (!unavailableItems || unavailableItems.length === 0) return;
      
      // Create a detailed message listing each unavailable product
      const buttonsError = this.querySelector('.product-form__buttons-error');
      if (buttonsError) {
        let errorMessage = 'The following items could not be added to your cart because they are sold out:';
        errorMessage += '<ul style="margin-top: 5px; margin-left: 15px;">';
        
        // For each unavailable item, try to get product info from its ID
        Promise.all(unavailableItems.map(item => 
          fetch(`/variants/${item.id}.json`)
            .then(response => response.json())
            .catch(() => ({ title: 'Product' })) // Fallback if fetch fails
        )).then(products => {
          products.forEach(product => {
            errorMessage += `<li>${product.title || 'Product'}</li>`;
          });
          errorMessage += '</ul>';
          
          // Show the error message
          buttonsError.innerHTML = errorMessage;
          buttonsError.removeAttribute('hidden');
          
          // Also update the standard error message for consistency
          this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
          if (this.errorMessageWrapper) {
            this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
            this.errorMessageWrapper.removeAttribute('hidden');
            if (this.errorMessage) {
              this.errorMessage.textContent = 'Some items could not be added to your cart.';
            }
          }
        });
      }
    }

    // Helper method to hide error messages
    hideErrorMessage() {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (this.errorMessageWrapper) {
        this.errorMessageWrapper.setAttribute('hidden', '');
      }
      
      // Also clear the buttons error div
      const buttonsError = this.querySelector('.product-form__buttons-error');
      if (buttonsError) {
        buttonsError.setAttribute('hidden', '');
        buttonsError.innerHTML = '';
      }
    }

    // Add main product from the form
    addMainProduct() {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      return fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .catch(error => {
          console.error('Error adding main product:', error);
          throw error;
        });
    }

    // Gather extra variant IDs from any inputs whose name starts with "extra_variant_id"
    getExtraVariantItems() {
      // Get both exact match and prefix match inputs
      const extraVariantInputs = [
        ...this.form.querySelectorAll('input[name="extra_variant_id"]'),
        ...this.form.querySelectorAll('input[name^="extra_variant_id_"]')
      ];
      
      console.log('Found extra variant inputs:', 
        extraVariantInputs.map(i => `${i.name}=${i.value}`).join(', '));
      
      const mainId = this.form.querySelector('[name="id"]')?.value;
      const items = [];
      
      extraVariantInputs.forEach(input => {
        const val = input.value && input.value.trim();
        if (!val) return;
        if (mainId && val === mainId) return; // avoid duplicating main variant
        if (!/^\d+$/.test(val)) return;
        items.push({ id: Number(val), quantity: 1 });
      });
      
      return items;
    }

    // Add all extra variants in a single request (batch)
    addAdditionalVariants(items) {
      if (!items || !items.length) {
        console.log('No extra items to add');
        return Promise.resolve({ response: {}, unavailableItems: [] });
      }
      
      console.log('Adding extra items to cart:', items);
      
      // Check availability of variants
      return this.checkVariantsAvailability(items)
        .then(result => {
          const availableItems = result.availableItems;
          const unavailableItems = result.unavailableItems;
          
          if (availableItems.length === 0) {
            console.log('No available bundle items to add');
            return { response: {}, unavailableItems };
          }
          
          // Try the Shopify AJAX API format for adding items
          const formData = new FormData();
          
          // Add each item using the standard Shopify cart add format
          availableItems.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity || 1);
          });
          
          // Only add sections if cart exists
          if (this.cart) {
            try {
              const sections = this.cart.getSectionsToRender();
              if (sections && Array.isArray(sections)) {
                formData.append('sections', sections.map((section) => section.id));
                formData.append('sections_url', window.location.pathname);
              }
            } catch (err) {
              console.warn('Could not get cart sections:', err);
            }
          }
          
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;
          
          return fetch(`${routes.cart_add_url}`, config)
            .then(response => {
              if (!response.ok) {
                console.error(`Error adding items: ${response.status} ${response.statusText}`);
                return response.json().then(data => {
                  console.error('Error details:', data);
                  return { response: {}, unavailableItems };
                }).catch(() => {
                  return { response: {}, unavailableItems };
                });
              }
              return response.json().then(data => {
                return { response: data, unavailableItems };
              });
            })
            .catch(error => {
              console.error('Failed to add extra items:', error);
              return { response: {}, unavailableItems };
            });
        });
    }

    // Check variant availability using /cart/add.js endpoint with no-op property
    checkVariantsAvailability(items) {
      if (!items || items.length === 0) {
        return Promise.resolve({ availableItems: [], unavailableItems: [] });
      }
      
      // Use a fast check: try to add items with quantity 0, which won't actually add them
      // but will tell us if they're available
      const availabilityChecks = items.map(item => {
        const formData = new FormData();
        formData.append('id', item.id);
        formData.append('quantity', 0); // Quantity 0 performs validation without adding to cart
        
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;
        
        return fetch(`${routes.cart_add_url}`, config)
          .then(response => {
            return response.json().then(data => {
              if (response.ok) {
                // Item is available
                return { item, available: true };
              } else {
                // Item is not available
                console.warn(`Variant ${item.id} is not available: ${data.description || 'Unknown error'}`);
                return { item, available: false, error: data.description };
              }
            }).catch(e => {
              console.error(`Error parsing response for variant ${item.id}:`, e);
              return { item, available: false, error: 'Error checking availability' };
            });
          })
          .catch(error => {
            console.error(`Network error checking variant ${item.id}:`, error);
            return { item, available: false, error: 'Network error' };
          });
      });
      
      return Promise.all(availabilityChecks)
        .then(results => {
          const availableItems = results.filter(r => r.available).map(r => r.item);
          const unavailableItems = results.filter(r => !r.available).map(r => r.item);
          
          if (unavailableItems.length > 0) {
            console.warn('Some bundle items are not available:', unavailableItems);
          }
          
          return { availableItems, unavailableItems };
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
